/**
 * GenericTopicPage — /topic/:key
 *
 * Renders any AI Topic that isn't scoped to a specific domain
 * (weather / location / poi). Powered by the same
 * `useAiTopic({ source: "generic" })` engine so related-topic chips
 * on ANY page can drill into a new topic with one tap.
 */
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import EntityMessageBoard from "@/components/EntityMessageBoard";
import AiTopicContent from "@/components/AiTopicContent";
import useAiTopic from "@/hooks/useAiTopic";

export default function GenericTopicPage() {
  const { key } = useParams();
  const title   = (key || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const ai = useAiTopic({
    source:    "generic",
    entityKey: key || "",
    title,
    context:   `Write a broad, evergreen overview of "${title}". Focus on what it is, why it matters, and how a general reader can learn more.`,
    deepLink:  `/topic/${key}`,
  });

  const palette = { from: "#EEF2FF", to: "#E0E7FF", accent: "#4338CA", ink: "#1E1B4B" };

  return (
    <main className="max-w-3xl mx-auto px-5 lg:px-8 py-8" data-testid={`generic-topic-${key}`}>
      <nav className="text-xs opacity-70 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-2">/</span>
        <span>Topic</span>
        <span className="mx-2">/</span>
        <span>{title}</span>
      </nav>

      <div
        className="rounded-2xl p-6 border mb-6"
        style={{
          borderColor: `${palette.accent}33`,
          background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)`,
        }}
      >
        <div
          className="text-xs font-mono uppercase tracking-widest mb-2 flex items-center gap-1"
          style={{ color: palette.ink }}
        >
          <Sparkles className="w-3 h-3" /> Community Knowledge Topic
        </div>
        <h1
          className="font-display leading-[1.05] tracking-tight"
          style={{ color: palette.ink, fontSize: "clamp(2rem, 4.5vw, 3rem)" }}
          data-testid="generic-topic-title"
        >
          {title}
        </h1>
      </div>

      <AiTopicContent doc={ai.doc} status={ai.status} retry={ai.retry} title={title} />

      <div className="mt-10 pt-6 border-t" style={{ borderColor: "var(--wofy-border)" }}>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
          style={{ color: palette.accent }}
          data-testid="generic-topic-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>
      </div>

      <EntityMessageBoard
        entityType="topic"
        entityId={key || "topic"}
        title={`Discuss · ${title}`}
      />
    </main>
  );
}
